/**
 * Profile cosmetics catalog — Title Cards + Emblems.
 *
 * Ported 1:1 from the OG `WINR-CURRENT/js/data.js` (`TITLES`, `EMBLEM_SVGS`,
 * `EMBLEMS`). This is static domain data, not a backend table: a `profiles` row
 * only stores `title_id` / `emblem_id`, which reference the entries here (same
 * ids the OG persists). Bg images + SVGs are kept identical for visual parity.
 *
 * `unlock`:
 *  - `'unlocked'` — selectable by anyone.
 *  - `'level:N'`  — gated behind a level the V2 progression system doesn't model
 *                   yet, so it renders locked.
 *  - `'winr+'`    — gated behind WINR+ (also locked for now).
 */
import { titleAssetUrl } from './titleAssets';

export type Unlock = 'unlocked' | `level:${number}` | 'winr+';
export type TitleAnim = 'kenBurns' | 'grain' | 'gradientDrift' | null;

export interface TitleCard {
  id: string;
  name: string;
  /** Background image shown behind the preview card. */
  bg: string;
  anim: TitleAnim;
  unlock: Unlock;
  /** Default emblem glow when this title has no emblem accent. */
  defaultGlow: string;
}

export interface Emblem {
  id: string;
  name: string;
  /** Inline SVG markup (single-path icons, `currentColor`). */
  svg: string;
  glow: string;
  unlock: Unlock;
}

export const TITLES: TitleCard[] = [
  {
    id: 'title1',
    name: 'Title 01',
    bg: titleAssetUrl(1),
    anim: null,
    unlock: 'unlocked',
    defaultGlow: '#FFFFFF',
  },
  {
    id: 'title2',
    name: 'Title 02',
    bg: titleAssetUrl(2),
    anim: 'kenBurns',
    unlock: 'unlocked',
    defaultGlow: '#54b9fe',
  },
  {
    id: 'title3',
    name: 'Title 03',
    bg: titleAssetUrl(3),
    anim: 'grain',
    unlock: 'unlocked',
    defaultGlow: '#f97316',
  },
  {
    id: 'title4',
    name: 'Title 04',
    bg: titleAssetUrl(4),
    anim: 'gradientDrift',
    unlock: 'unlocked',
    defaultGlow: '#fbbf24',
  },
  {
    id: 'title5',
    name: 'Title 05',
    bg: titleAssetUrl(5),
    anim: 'grain',
    unlock: 'unlocked',
    defaultGlow: '#a855f7',
  },
  {
    id: 'title6',
    name: 'Title 06',
    bg: titleAssetUrl(6),
    anim: 'kenBurns',
    unlock: 'unlocked',
    defaultGlow: '#f97316',
  },
  {
    id: 'title7',
    name: 'Title 07',
    bg: titleAssetUrl(7),
    anim: null,
    unlock: 'unlocked',
    defaultGlow: '#ef4444',
  },
  {
    id: 'title8',
    name: 'Title 08',
    bg: titleAssetUrl(8),
    anim: null,
    unlock: 'unlocked',
    defaultGlow: '#22d3ee',
  },
];

export const TITLE_VARIANT_GROUPS = [['title3', 'title4']] as const;

export function getTitleFamilies(): TitleCard[][] {
  const grouped = new Set<string>(TITLE_VARIANT_GROUPS.flat());
  const families = TITLES.filter((title) => !grouped.has(title.id)).map((title) => [title]);

  TITLE_VARIANT_GROUPS.forEach((group) => {
    const variants = group
      .map((id) => TITLES.find((title) => title.id === id))
      .filter((title): title is TitleCard => Boolean(title));
    if (variants.length > 0) families.splice(2, 0, variants);
  });

  return families;
}

const EMBLEM_SVGS = {
  apex: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"><path d="M12 2.5L22 20.5H2L12 2.5z"/><path d="M12 9L17.5 19H6.5L12 9z" opacity="0.55"/><circle cx="12" cy="15.5" r="1.2" fill="currentColor"/></svg>`,
  crosshair: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="4.5"/><line x1="12" y1="1" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="23"/><line x1="1" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="23" y2="12"/><circle cx="12" cy="12" r="0.9" fill="currentColor"/></svg>`,
  crown: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"><path d="M2 19l1.5-11 4.5 5L12 4l4 9 4.5-5L22 19H2z"/><line x1="3" y1="22" x2="21" y2="22"/><circle cx="12" cy="4" r="1" fill="currentColor"/><circle cx="3.5" cy="8" r="0.8" fill="currentColor"/><circle cx="20.5" cy="8" r="0.8" fill="currentColor"/></svg>`,
  phoenix: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" stroke-linecap="round"><path d="M12 22c-3.2-2-6-6-6-10 0-2.8 1.8-4.6 3.7-4.6 1.2 0 2.3.9 2.3 2.1 0-1.2 1.1-2.1 2.3-2.1C16.2 7.4 18 9.2 18 12c0 4-2.8 8-6 10z"/><path d="M3 8.5c2.3-1 4.7-1 6.5 1.2"/><path d="M21 8.5c-2.3-1-4.7-1-6.5 1.2"/><path d="M12 14.5v4.5"/></svg>`,
  bolt: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"><path d="M14 2L4 14h7l-2 8 11-14h-7l1-6z"/></svg>`,
  infinity: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M6 16c-2.2 0-4-1.8-4-4s1.8-4 4-4c2.5 0 4 2.5 6 4 2 1.7 3.5 4 6 4 2.2 0 4-1.8 4-4s-1.8-4-4-4c-2.5 0-4 2.3-6 4-2 1.5-3.5 4-6 4z"/><circle cx="2.5" cy="12" r="1" fill="currentColor"/><circle cx="21.5" cy="12" r="1" fill="currentColor"/><circle cx="12" cy="12" r="0.9" fill="currentColor"/></svg>`,
  reaper: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" stroke-linecap="round"><path d="M5 21L20 4"/><path d="M16 2.5c2 0 4.2 1.8 4.5 4-2.2 2-6.3 1.8-8.5-.2.3-2.2 2-3.8 4-3.8z"/><line x1="3" y1="22" x2="6" y2="20"/><circle cx="14.5" cy="6" r="0.9" fill="currentColor"/></svg>`,
  diamond: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"><path d="M6 4h12l4 5-10 13L2 9l4-5z"/><line x1="6" y1="4" x2="9" y2="9"/><line x1="18" y1="4" x2="15" y2="9"/><line x1="12" y1="4" x2="12" y2="9"/><line x1="2" y1="9" x2="22" y2="9"/><line x1="9" y1="9" x2="12" y2="22"/><line x1="15" y1="9" x2="12" y2="22"/></svg>`,
  ouroboros: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" stroke-linecap="round"><path d="M12 3a9 9 0 1 1-8.5 6"/><path d="M12 3l3 2-3 2"/><circle cx="4.5" cy="14" r="1.3" fill="currentColor"/><path d="M4 12.5c0 0 1 1 1.5 0"/></svg>`,
  circuit: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><rect x="8" y="8" width="8" height="8" rx="1"/><line x1="12" y1="2" x2="12" y2="8"/><line x1="12" y1="16" x2="12" y2="22"/><line x1="2" y1="12" x2="8" y2="12"/><line x1="16" y1="12" x2="22" y2="12"/><circle cx="12" cy="3" r="1.5" fill="currentColor"/><circle cx="12" cy="21" r="1.5" fill="currentColor"/><circle cx="3" cy="12" r="1.5" fill="currentColor"/><circle cx="21" cy="12" r="1.5" fill="currentColor"/></svg>`,
};

export const EMBLEMS: Emblem[] = [
  { id: 'apex', name: 'Apex Triangle', svg: EMBLEM_SVGS.apex, glow: '#FFFFFF', unlock: 'unlocked' },
  {
    id: 'crosshair',
    name: 'Crosshair',
    svg: EMBLEM_SVGS.crosshair,
    glow: '#ef4444',
    unlock: 'unlocked',
  },
  { id: 'crown', name: 'Crown', svg: EMBLEM_SVGS.crown, glow: '#fbbf24', unlock: 'unlocked' },
  { id: 'phoenix', name: 'Phoenix', svg: EMBLEM_SVGS.phoenix, glow: '#f97316', unlock: 'unlocked' },
  { id: 'bolt', name: 'Bolt', svg: EMBLEM_SVGS.bolt, glow: '#facc15', unlock: 'level:3' },
  {
    id: 'infinity',
    name: 'Infinity',
    svg: EMBLEM_SVGS.infinity,
    glow: '#00FFE5',
    unlock: 'level:6',
  },
  { id: 'reaper', name: 'Reaper', svg: EMBLEM_SVGS.reaper, glow: '#22c55e', unlock: 'level:10' },
  {
    id: 'diamond',
    name: 'Diamond Cut',
    svg: EMBLEM_SVGS.diamond,
    glow: '#ffffff',
    unlock: 'level:14',
  },
  {
    id: 'ouroboros',
    name: 'Ouroboros',
    svg: EMBLEM_SVGS.ouroboros,
    glow: '#B794F6',
    unlock: 'winr+',
  },
  { id: 'circuit', name: 'Circuit', svg: EMBLEM_SVGS.circuit, glow: '#39FF14', unlock: 'winr+' },
];

/** OG defaults when a profile has no `title_id` / `emblem_id` (supabase.js:96-97). */
export const DEFAULT_TITLE_ID = 'title1';
export const DEFAULT_EMBLEM_ID = 'crown';

export function getTitle(id?: string | null): TitleCard {
  return TITLES.find((t) => t.id === id) ?? TITLES.find((t) => t.id === DEFAULT_TITLE_ID)!;
}

export function getEmblem(id?: string | null): Emblem {
  return EMBLEMS.find((e) => e.id === id) ?? EMBLEMS.find((e) => e.id === DEFAULT_EMBLEM_ID)!;
}

export function isUnlocked(unlock: Unlock): boolean {
  return unlock === 'unlocked';
}

/** Human label for a locked cost chip, matching the OG copy. */
export function unlockLabel(unlock: Unlock): string {
  if (unlock === 'unlocked') return 'Unlocked';
  if (unlock === 'winr+') return '◆ WINR+';
  return `🔒 Level ${unlock.split(':')[1]}`;
}
